import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Loader2, LucideIcon, X } from 'lucide-react';
import { ReactNode, useState } from 'react';

import {
  FINCHGPT_EVENTS,
  FINCHGPT_PROPERTIES,
} from '@/constants/analytics/finchgpt-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';

import { BaseToolBadgeProps } from './types';

interface BaseBadgeTextResultTexts {
  loading: string;
  done: string;
  error: string;
}

interface BaseBadgeProps extends BaseToolBadgeProps {
  icon?: LucideIcon;
  children?: ReactNode;
  showArgs?: boolean;
  resultTexts: BaseBadgeTextResultTexts;
}

export const BaseBadge = ({
  isLoading = false,
  isDone = false,
  isError = false,
  icon: Icon,
  children,
  args = {},
  showArgs = true,
  resultTexts,
  matterId,
  threadId,
}: BaseBadgeProps) => {
  const capture = usePostHogCapture();
  const [isExpanded, setIsExpanded] = useState(false);

  const argEntries = showArgs ? Object.entries(args) : [];
  const hasExpandableContent = argEntries.length > 0 || children;

  // Determine the current state
  let state: 'loading' | 'done' | 'error' | 'default' = 'default';
  if (isError) {
    state = 'error';
  } else if (isLoading) {
    state = 'loading';
  } else if (isDone) {
    state = 'done';
  }

  const resultText =
    state === 'error'
      ? resultTexts.error
      : state === 'loading'
        ? resultTexts.loading
        : state === 'done'
          ? resultTexts.done
          : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.15 }}
      className={cn(
        'rounded-md border overflow-hidden',
        'transition-colors duration-150',
        state === 'done' && 'bg-emerald-50/50 border-emerald-200/60',
        state === 'error' && 'bg-red-50/50 border-red-200/60',
        state === 'loading' && 'bg-slate-50/50 border-slate-200/60',
        state === 'default' && 'bg-slate-50/50 border-slate-200/60',
      )}
    >
      {/* Main row - always visible */}
      <button
        type="button"
        onClick={() => {
          if (hasExpandableContent) {
            const newExpanded = !isExpanded;
            setIsExpanded(newExpanded);
            if (newExpanded) {
              capture(FINCHGPT_EVENTS.TOOL_CALL_EXPANDED, {
                [FINCHGPT_PROPERTIES.MATTER_ID]: matterId,
                [FINCHGPT_PROPERTIES.THREAD_ID]: threadId,
                [FINCHGPT_PROPERTIES.TOOL_NAME]: resultTexts.done,
                [FINCHGPT_PROPERTIES.TOOL_STATUS]: state,
              });
            }
          }
        }}
        disabled={!hasExpandableContent}
        className={cn(
          'w-full flex items-center gap-1.5 px-2 py-1',
          'text-left',
          hasExpandableContent && 'cursor-pointer hover:bg-black/[0.02]',
          !hasExpandableContent && 'cursor-default',
        )}
      >
        {/* Status indicator */}
        <div
          className={cn(
            'flex-shrink-0 w-4 h-4 rounded flex items-center justify-center',
            state === 'done' && 'bg-emerald-100 text-emerald-600',
            state === 'error' && 'bg-red-100 text-red-600',
            state === 'loading' && 'bg-slate-100 text-slate-500',
            state === 'default' && 'bg-slate-100 text-slate-500',
          )}
        >
          {state === 'loading' ? (
            <Loader2 className="w-2.5 h-2.5 animate-spin" />
          ) : state === 'done' ? (
            <Check className="w-2.5 h-2.5" strokeWidth={2.5} />
          ) : state === 'error' ? (
            <X className="w-2.5 h-2.5" strokeWidth={2.5} />
          ) : Icon ? (
            <Icon className="w-2.5 h-2.5" />
          ) : null}
        </div>

        {/* Tool name */}
        <span
          className={cn(
            'flex-1 text-[11px] font-medium truncate',
            state === 'done' && 'text-emerald-700',
            state === 'error' && 'text-red-700',
            state === 'loading' && 'text-slate-600',
            state === 'default' && 'text-slate-600',
          )}
        >
          {resultText}
        </span>

        {/* Expand indicator */}
        {hasExpandableContent && (
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.15 }}
            className="flex-shrink-0"
          >
            <ChevronDown
              className={cn(
                'w-3 h-3',
                state === 'done' && 'text-emerald-400',
                state === 'error' && 'text-red-400',
                (state === 'loading' || state === 'default') && 'text-slate-400',
              )}
            />
          </motion.div>
        )}
      </button>

      {/* Expandable content */}
      <AnimatePresence initial={false}>
        {isExpanded && hasExpandableContent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div
              className={cn(
                'px-2 pb-1.5 pt-0.5',
                'border-t',
                state === 'done' && 'border-emerald-100/80',
                state === 'error' && 'border-red-100/80',
                (state === 'loading' || state === 'default') &&
                  'border-slate-100/80',
              )}
            >
              {/* Arguments */}
              {argEntries.length > 0 && (
                <div className="space-y-0.5">
                  {argEntries.map(([key, value]) => (
                    <div
                      key={key}
                      className="flex items-start gap-1.5 text-[10px]"
                    >
                      <span className="text-slate-400 font-medium shrink-0">
                        {key}:
                      </span>
                      <span className="text-slate-600 truncate font-mono">
                        {typeof value === 'string'
                          ? value
                          : JSON.stringify(value)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Custom children content */}
              {children && <div className="mt-1">{children}</div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
